// The side panel: every finding of one file, per Pasal, each opening into the
// look Legal chose (option B): the clause in both languages with the words
// marked or the edit struck through, and the comment card beside it.
//
// It opens at 70% of the screen and pushes the page aside; dragged wider than
// the page can spare, it covers the page instead of squeezing it; dragged to
// the left edge it is full screen. Width, full screen and language are
// remembered in this browser. Nothing here can stop a submit.

import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { requestTranslation, toggleChallenge } from '../legalCheckStore';
import WORDS from './words';
import {
  LEVELS, atOf, editList, findingsOf, fixOf, matches, ordered, pick, pieces, placeOf, sectionHead, titleOf,
} from './reviewModel';
import {
  closePanel, dismissTip, focus, pickFilter, setFull, setLang, setWidth, toast, toggle,
  toggleMinor, toggleWhole, useReview, view,
} from './reviewStore';
import { AiMark, CmpStrip, NewTag, SevChip, Tabs, lc } from './parts';
import './review.css';

const MIN_W = 320;
// With less room than this between the sidebar and the panel, the panel covers the
// page instead of pushing it: helpdesk's pages keep their columns side by side
// at any width, so a narrower page is crushed rather than rearranged.
const FORM_NEEDS = 640;
const ICON = {
  max: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"><path d="M14 4h6v6M10 20H4v-6M20 4l-7 7M4 20l7-7" /></svg>,
  min: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"><path d="M4 14h6v6M20 10h-6V4M14 10l7-7M10 14l-7 7" /></svg>,
  close: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"><path d="M6 6l12 12M18 6L6 18" /></svg>,
  chev: <svg className="lr-chev" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" aria-hidden="true"><path d="M9 6l6 6-6 6" /></svg>,
  list: <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><path d="M9 6h11M9 12h11M9 18h11M4 6h.01M4 12h.01M4 18h.01" /></svg>,
  copy: <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><rect x="9" y="9" width="12" height="12" rx="2" /><path d="M5 15V5a2 2 0 0 1 2-2h10" /></svg>,
};

const vw = () => document.documentElement.clientWidth;
// Where the page starts: right of the sidebar.
const pageLeft = () => {
  const el = document.querySelector('.content-wrapper');
  return el ? Math.max(0, el.getBoundingClientRect().left) : 0;
};
const defaultWidth = () => Math.min(Math.max(560, vw() * 0.7), 1400);

async function copyText(text, ok, failed) {
  try { await navigator.clipboard.writeText(text); toast(ok); } catch (e) { toast(failed); }
}

// A clause in one language, with this finding's words marked.
function Para({ text, lang, f, w, ctx, hl }) {
  const parts = f ? pieces(text, lang, f.marks, f.edit) : [{ kind: 'plain', text }];
  const body = parts.map((p, i) => {
    if (p.kind === 'mark') return <mark key={i}>{p.text}</mark>;
    if (p.kind === 'del') return <React.Fragment key={i}><del>{p.text}</del>{p.to ? <ins>{p.to}</ins> : null}</React.Fragment>;
    if (p.kind === 'add') return <React.Fragment key={i}>{' '}<ins>{p.to}</ins></React.Fragment>;
    return <React.Fragment key={i}>{p.text}</React.Fragment>;
  });
  return (
    <p className={ctx ? 'ctx' : ''}>
      {w.dl[lang] && <span className="dl">{w.dl[lang]}</span>}
      {hl ? <span className="hl">{body}</span> : body}
    </p>
  );
}

function Detail({ f, layout, v, uid, st, w, onChallenge }) {
  const unit = f.unit && layout && layout.units ? layout.units[f.unit] : null;
  const sec = f.section && layout ? (layout.sections || []).find((s) => s.key === f.section) : null;
  const whole = v.whole.has(f.key);
  const secUnits = sec ? sec.units.filter((k) => layout.units[k]) : [];
  const shown = whole ? secUnits : (unit ? [f.unit] : []);
  const langs = ['en', 'id', 'text'];
  const place = placeOf(f, st.lang, w);
  return (
    <div className="lr-item-detail">
      <div className="lr-b-row">
        <div className="lr-doc">
          {shown.length === 0 && (f.quote ? <p className="quote">{f.quote}</p> : <p className="ctx">{w.row(f.row)}</p>)}
          {shown.map((k) => {
            const u = layout.units[k];
            const own = k === f.unit;
            return langs.filter((lg) => u.texts && u.texts[lg]).map((lg) => (
              <Para key={`${k}${lg}`} text={u.texts[lg]} lang={lg} f={own ? f : null} w={w}
                    ctx={!own} hl={own && shown.length > 1} />
            ));
          })}
        </div>
        <aside className={`lr-bubble ${lc(f.sev)}${f.src === 'ai' ? ' ai' : ''}`}>
          <div className="lr-b-top">
            <SevChip sev={f.sev} />
            <span className={`lr-src${f.src === 'ai' ? ' ai' : ''}`} title={f.src === 'ai' ? w.aiTip : undefined}>{w.src[f.src]}</span>
            {f.src === 'machine' && f.aiAgrees && <span className="lr-src ai" title={w.aiTip}>✦ {w.aiAgrees}</span>}
          </div>
          <div className="lr-b-loc">{[place, w.lang[f.col], f.cmp ? w.fromCmp : ''].filter(Boolean).join(' · ')}</div>
          <p className="lr-b-title">{titleOf(f, st.lang)}</p>
          {fixOf(f, st.lang) && <p className="lr-b-fix"><span className="lr-fix-label">{w.suggested}</span>{fixOf(f, st.lang)}</p>}
          {f.src === 'machine' && f.aiNote && <p className="lr-b-note">✦ {w.aiAgrees}: {pick(f.aiNote, st.lang)}</p>}
          {f.notes.map((n, i) => <p key={i} className="lr-b-note">{pick(n, st.lang)}</p>)}
          <div className="lr-b-act">
            <button className="lr-mini" type="button" onClick={() => copyText(place, w.copied(place), w.copyFailed)}>{ICON.copy}{w.copyLoc}</button>
            <button className="lr-mini dismiss" type="button" onClick={() => onChallenge(f)}>{f.challenged ? w.undo : w.notIssue}</button>
          </div>
          {secUnits.length > 1 && (
            <div className="lr-more">
              <button className="lr-link" type="button" onClick={() => toggleWhole(uid, f.key)}>
                {whole ? w.lessSection : w.wholeSection(pick(sec.label, st.lang))}
              </button>
            </div>
          )}
        </aside>
      </div>
    </div>
  );
}

function ReviewPanel({ source, entries }) {
  const st = useReview();
  const w = WORDS[st.lang];
  const here = st.panel.open && st.panel.source === source;
  const entry = here ? entries.find((e) => e.uid === st.panel.uid) : null;
  const data = entry && entry.state === 'ready' ? entry.data : null;
  const open = !!(here && data && data.rules && data.rules.status === 'done');
  const v = entry ? view(entry.uid) : null;
  const bodyRef = useRef(null);
  const scrollTo = useRef(null);
  const [modal, setModal] = useState(null);
  const [, redraw] = useState(0);

  // The file went away (removed, submitted): the panel goes with it.
  useEffect(() => { if (here && !entry) closePanel(); }, [here, entry]);

  // Width and whether the page makes room or is covered.
  const target = st.full ? vw() : (parseFloat(st.width) || defaultWidth());
  const cover = st.full || target > vw() - pageLeft() - FORM_NEEDS;
  useEffect(() => {
    if (st.width) document.documentElement.style.setProperty('--lr-pw', st.width);
    else document.documentElement.style.removeProperty('--lr-pw');
  }, [st.width]);
  useEffect(() => {
    document.body.classList.toggle('lr-open', open && !cover);
    return () => document.body.classList.remove('lr-open');
  }, [open, cover]);
  useEffect(() => {
    const onResize = () => redraw((n) => n + 1);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const all = data ? findingsOf(data) : [];
  const layout = data ? data.layout : null;
  const visible = (f) => (!f.minor || (v && v.minor)) && matches(f, v.level, v.source);
  const navList = data ? ordered(all.filter(visible), layout) : [];
  const idx = v ? navList.findIndex((f) => f.key === v.active) : -1;

  // Bring the finding just focused into view, once it is drawn.
  useEffect(() => {
    if (!open || !scrollTo.current) return;
    const el = document.getElementById(`lr-it-${scrollTo.current}`);
    scrollTo.current = null;
    if (el) el.scrollIntoView({ block: 'start', behavior: 'smooth' });
  });
  // Opened from the box on a finding: show it.
  const lastOpen = useRef(null);
  useEffect(() => {
    if (open && v && v.active && lastOpen.current !== `${st.panel.uid}:${v.active}`) {
      lastOpen.current = `${st.panel.uid}:${v.active}`;
      scrollTo.current = v.active;
      redraw((n) => n + 1);
    }
    if (!open) lastOpen.current = null;
  }, [open, st.panel.uid, v && v.active]); // eslint-disable-line react-hooks/exhaustive-deps

  const step = (d) => {
    if (!navList.length) return;
    let i = idx < 0 ? 0 : Math.max(0, Math.min(navList.length - 1, idx + d));
    const f = navList[i];
    if (f.minor && !v.minor) toggleMinor(entry.uid);
    focus(entry.uid, f.key);
    lastOpen.current = `${st.panel.uid}:${f.key}`;
    scrollTo.current = f.key;
  };

  // Escape closes; arrows walk the findings (not while typing in the form).
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') { if (modal) setModal(null); else closePanel(); return; }
      const typing = /^(INPUT|TEXTAREA|SELECT)$/.test((document.activeElement || {}).tagName || '')
        || (document.activeElement && document.activeElement.isContentEditable);
      if (typing || e.ctrlKey || e.metaKey || e.altKey) return;
      if (e.key === 'ArrowRight') { e.preventDefault(); step(1); }
      if (e.key === 'ArrowLeft') { e.preventDefault(); step(-1); }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  });

  // The divider drags all the way: from a narrow panel to full screen (the left
  // edge snaps to full screen); double-click goes back to the default width.
  const onGripDown = (e) => {
    e.preventDefault();
    dismissTip();
    const g = e.currentTarget;
    try { g.setPointerCapture(e.pointerId); } catch (err) { /* works without capture */ }
    document.body.classList.add('lr-dragging');
    let full = st.full;
    let px = '';
    const move = (ev) => {
      if (ev.clientX < 40) { if (!full) { full = true; setFull(true); } return; }
      if (full) { full = false; setFull(false); }
      const wpx = Math.round(Math.max(MIN_W, Math.min(vw(), vw() - ev.clientX)));
      px = `${wpx}px`;
      document.documentElement.style.setProperty('--lr-pw', px);
      document.body.classList.toggle('lr-open', wpx <= vw() - pageLeft() - FORM_NEEDS);
    };
    const up = () => {
      g.removeEventListener('pointermove', move);
      g.removeEventListener('pointerup', up);
      document.body.classList.remove('lr-dragging');
      setWidth(px || st.width, full);
    };
    g.addEventListener('pointermove', move);
    g.addEventListener('pointerup', up);
  };

  const panel = (
    <aside className={`lr-panel${open ? ' on' : ''}${st.full ? ' full' : ''}${open && st.tip ? ' tip' : ''}`}
           aria-label={w.title} aria-hidden={!open}>
      {open && renderBody()}
    </aside>
  );

  function renderBody() {
    const base = all.filter((f) => !f.minor);
    const noFilter = v.level === 'all' && v.source === 'all';
    const sections = (layout && layout.sections) || [];
    const bySection = new Map();
    navList.forEach((f) => {
      const k = f.section && sections.some((s) => s.key === f.section) ? f.section : '~other';
      if (!bySection.has(k)) bySection.set(k, []);
      bySection.get(k).push(f);
    });
    const minorCount = all.filter((f) => f.minor).length;
    const onChallenge = (f) => {
      if (!f.challenged) toast(w.challenged);
      toggleChallenge(source, entry.uid, f.raw);
    };
    const item = (f) => {
      const isOpen = v.opened.has(f.key);
      return (
        <div key={f.key} id={`lr-it-${f.key}`}
             className={`lr-item ${lc(f.sev)}${f.src === 'ai' ? ' ai' : ''}${isOpen ? ' open' : ''}${v.active === f.key ? ' active' : ''}${f.challenged ? ' gone' : ''}`}>
          <button className="lr-item-row" type="button" aria-expanded={isOpen}
                  onClick={() => {
                    // Opening one closes the other; the list moves up when the
                    // one above closes, so the one just opened is brought into view.
                    if (toggle(entry.uid, f.key)) {
                      lastOpen.current = `${st.panel.uid}:${f.key}`;
                      scrollTo.current = f.key;
                    }
                  }}>
            <SevChip sev={f.sev} />
            <AiMark f={f} w={w} />
            <span className="lr-item-loc">{atOf(f, st.lang, w)}<NewTag on={v.fresh.has(f.key)} w={w} /></span>
            <span className="lr-item-t">{titleOf(f, st.lang)}</span>
            {ICON.chev}
          </button>
          {isOpen && <Detail f={f} layout={layout} v={v} uid={entry.uid} st={st} w={w} onChallenge={onChallenge} />}
        </div>
      );
    };
    let list = [];
    sections.forEach((s) => {
      const fs = bySection.get(s.key);
      if (!fs) {
        if (noFilter && ['pasal', 'item'].includes(s.kind)) {
          list.push(
            <div key={s.key} className="lr-clean"><b>{pick(s.label, st.lang)}</b>
              {(s.title && (s.title[st.lang] || s.title.text || s.title.en || s.title.id)) || ''}
              <span className="ok">{w.nothing}</span></div>,
          );
        }
        return;
      }
      const h = sectionHead(s, st.lang);
      list.push(
        <div key={s.key} className="lr-grp">
          {h.bab && <span className="lr-grp-bab">{h.bab}</span>}
          <span className="lr-grp-n">{h.main}</span>{h.sub && <span className="lr-grp-en">{h.sub}</span>}
        </div>,
      );
      list = list.concat(fs.map(item));
    });
    if (bySection.has('~other')) {
      list.push(<div key="~other" className="lr-grp"><span className="lr-grp-n">{w.other.toUpperCase()}</span></div>);
      list = list.concat(bySection.get('~other').map(item));
    }
    const i = idx;
    return (
      <>
        <div className="lr-grip" title={w.grip} onPointerDown={onGripDown}
             onDoubleClick={() => { document.documentElement.style.removeProperty('--lr-pw'); setWidth('', false); }} />
        {st.tip && (
          <div className="lr-tip" role="dialog" aria-label={w.tipTitle}>
            <b><span className="lr-nudge" aria-hidden="true">⟷</span>{w.tipTitle}</b>
            <p>{w.tipText}</p>
            <button className="lr-btn primary" type="button" onClick={dismissTip}>{w.tipOk}</button>
          </div>
        )}
        <div className="lr-p-head" onDoubleClick={(e) => { if (!e.target.closest('button')) setFull(!st.full); }}>
          <div className="lr-p-title"><b>{w.title}</b><span>{entry.name}</span></div>
          <div className="lr-lang" role="group" aria-label={w.langPick}>
            {['id', 'en'].map((l) => (
              <button key={l} type="button" aria-pressed={st.lang === l} onClick={() => setLang(l)}>{l.toUpperCase()}</button>
            ))}
          </div>
          <button className="lr-icon" type="button" title={st.full ? w.restore : w.max} aria-label={st.full ? w.restore : w.max}
                  onClick={() => setFull(!st.full)}>{st.full ? ICON.min : ICON.max}</button>
          <button className="lr-icon" type="button" title={w.close} aria-label={w.close} onClick={closePanel}>{ICON.close}</button>
        </div>
        <div className="lr-p-filter">
          <Tabs findings={all} level={v.level} source={v.source} w={w}
                onPick={(k) => { pickFilter(entry.uid, k, LEVELS); if (bodyRef.current) bodyRef.current.scrollTop = 0; }} />
        </div>
        <div className="lr-p-prog"><i style={{ width: `${navList.length && i >= 0 ? ((i + 1) / navList.length) * 100 : 0}%` }} /></div>
        <div className="lr-p-body" ref={bodyRef}>
          <div className="lr-p-inner">
            <CmpStrip data={data} w={w} onStart={() => requestTranslation(source, entry.uid)} />
            {list.length ? list : <p className="lr-empty">{base.length ? w.empty : w.noFindings}</p>}
            {noFilter && minorCount > 0 && (
              <div className="lr-tail">
                <button className="lr-link" type="button" onClick={() => toggleMinor(entry.uid)}>{w.minor(!v.minor, minorCount)}</button>
              </div>
            )}
          </div>
        </div>
        <div className="lr-p-foot">
          <button className="lr-btn quiet" type="button" title={w.editList}
                  onClick={() => setModal(editList(ordered(all, layout), layout, st.lang, w, entry.name))}>
            {ICON.list}<span className="lr-lbl">{w.editList}</span>
          </button>
          <span className="lr-grow" />
          <span className="lr-count">{i >= 0 ? w.of(i + 1, navList.length) : w.findings(navList.length)}</span>
          <button className="lr-btn quiet" type="button" title={w.prev} disabled={i <= 0} onClick={() => step(-1)}>‹</button>
          <button className="lr-btn primary" type="button" disabled={navList.length > 0 && i === navList.length - 1}
                  onClick={() => step(1)}>{w.next}</button>
        </div>
      </>
    );
  }

  return ReactDOM.createPortal(
    <>
      {panel}
      {modal !== null && (
        <div className="lr-modal-bg" role="dialog" aria-modal="true" onClick={(e) => { if (e.target === e.currentTarget) setModal(null); }}>
          <div className="lr-modal">
            <div className="lr-modal-h">
              <h3>{w.listTitle}</h3>
              <button className="lr-btn primary" type="button" onClick={() => copyText(modal, w.listCopied, w.copyFailed)}>{w.copy}</button>
              <button className="lr-btn quiet" type="button" onClick={() => setModal(null)}>{w.close}</button>
            </div>
            <pre>{modal}</pre>
          </div>
        </div>
      )}
      {st.toast && <div className="lr-toast" role="status" key={st.toast.n}>{st.toast.text}</div>}
    </>,
    document.body,
  );
}

export default ReviewPanel;
